// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React, {
  useEffect,
  useState,
} from 'react';

import {
  useDispatch,
  useSelector,
} from 'react-redux';
import { useLocation } from 'react-router';

import {
  SearchOutlined,
  SearchSharp,
} from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  OutlinedInput,
  Pagination,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import {
  useGetConnectorCategoriesQuery,
  useGetConnectorLabelsQuery,
  useGetConnectorMetaQuery,
  useGetConnectorProductsQuery,
  useGetConnectorVersionsQuery,
  useSearchConnectorsQuery,
} from '../apis/connectorApis';
import ConnectorCard from '../components/ConnectorCard';
import CustomSnackbar from '../components/CustomSnackbar';
import PageFooterContent from '../components/PageFooterContent';
import { setCategories } from '../store/categorySlice';
import { setConnectors } from '../store/connectorSlice';
import { setLabels } from '../store/labelSlice';
import { setProducts } from '../store/productSlice';
import { RootState } from '../store/rootReducer';
import { setVersions } from '../store/versionSlice';
import {
  Connector,
  ConnectorMeta,
} from '../types/types';
import {
  IDENTITY_SERVER,
  MICRO_INTEGRATOR,
} from '../utils/constants';
import { getApiErrorMsg } from '../utils/utils';

const ITEM_PER_PAGE_SIZES = [12, 20, 100];
const PAGE = "page";
const SEARCH = "search";
const PRODUCT = "product";
const VERSION = "version";
const CATEGORY = "category";
const PRODUCT_MAP: { [key: string]: string } = {
  IS: IDENTITY_SERVER,
  MI: MICRO_INTEGRATOR,
};
export const LABEL = "label";

const Home: React.FC = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [page, setPage] = useState<number>(1);
  const [searchValue, setSearchValue] = useState("");
  const [productValue, setProductValue] = useState("");
  const [versionValue, setVersionValue] = useState("");
  const [categoryValue, setCategoryValue] = useState("");
  const [labelValue, setLabelValue] = useState("");
  const [isSnackbarOpen, setIsSnackbarOpen] = useState<boolean>(false);
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    ITEM_PER_PAGE_SIZES[0]
  );
  const isMobileScreen = useMediaQuery("(max-width:37.5rem)");
  const location = useLocation();
  const connectors = useSelector((state: RootState) => state.connectors);
  const categories = useSelector((state: RootState) => state.categories);
  const products = useSelector((state: RootState) => state.products);
  const versions = useSelector((state: RootState) => state.versions);
  const labels = useSelector((state: RootState) => state.labels);

  // Get product abbreviation (i.e: Micro Integrator to MI)
  const getProductAbbreviation = (productName: string): string => {
    const reverseMap = Object.fromEntries(
      Object.entries(PRODUCT_MAP).map(([key, value]) => [value, key])
    );
    return reverseMap[productName];
  };

  // Fetch connector information from Backend
  const { data: connectorMeta, error: connectorMetaError } =
    useGetConnectorMetaQuery({
      connectorName: searchValue,
      description: searchValue,
      product: getProductAbbreviation(productValue),
      version: versionValue,
      category: categoryValue,
      label: labelValue,
    });
  const {
    data: connectorsResponse,
    error: connectorError,
    isFetching: connectorLoading,
  } = useSearchConnectorsQuery(
    {
      limit: itemsPerPage,
      offset: (page - 1) * itemsPerPage,
      connectorName: searchValue,
      description: searchValue,
      product: getProductAbbreviation(productValue),
      version: versionValue,
      category: categoryValue,
      label: labelValue,
    },
    {
      skip: !!connectorMetaError,
    }
  );

  const {
    data: categoryResponse,
    error: categoryError,
    isFetching: categoryLoading,
  } = useGetConnectorCategoriesQuery(getProductAbbreviation(productValue));

  const {
    data: productResponse,
    error: productError,
    isLoading: productLoading,
  } = useGetConnectorProductsQuery();

  const {
    data: versionResponse,
    error: versionError,
    isFetching: versionLoading,
  } = useGetConnectorVersionsQuery(getProductAbbreviation(productValue), {
    skip: productValue === "",
  });

  const {
    data: labelResponse,
    error: labelError,
    isFetching: labelLoading,
  } = useGetConnectorLabelsQuery(getProductAbbreviation(productValue));

  useEffect(() => {
    window.scrollTo(0, 0);
  });

  useEffect(() => {
    let localItemsPerPage = localStorage.getItem("itemsPerPage");
    if (localItemsPerPage != null) {
      setItemsPerPage(Number(localItemsPerPage));
    }
  }, []);

  // Update the redux store with fetched information
  useEffect(() => {
    if (connectorsResponse) {
      dispatch(setConnectors(connectorsResponse));
    }
    if (connectorMetaError || connectorError) {
      setIsSnackbarOpen(true);
    }
  }, [connectorsResponse, connectorError, connectorMetaError, dispatch]);

  useEffect(() => {
    if (categoryResponse) {
      dispatch(setCategories(categoryResponse));
    }
    if (categoryError) {
      setIsSnackbarOpen(true);
    }
  }, [categoryResponse, categoryError]);

  useEffect(() => {
    if (productResponse) {
      dispatch(setProducts(productResponse));
    }
    if (productError) {
      setIsSnackbarOpen(true);
    }
  }, [productResponse, productError]);

  useEffect(() => {
    if (versionResponse) {
      dispatch(setVersions(versionResponse));
    }
    if (versionError) {
      setIsSnackbarOpen(true);
    }
  }, [versionResponse, versionError]);

  useEffect(() => {
    if (labelResponse) {
      dispatch(setLabels(labelResponse));
    }
    if (labelError) {
      setIsSnackbarOpen(true);
    }
  }, [labelResponse, labelError]);

  // Set the page value when URL gets changed
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const currentPage = queryParams.get(PAGE);
    setPage(currentPage ? Number(currentPage) : 1);

    const searchKey = queryParams.get(SEARCH);
    setSearchValue(searchKey ? searchKey : "");

    const product = queryParams.get(PRODUCT);
    setProductValue(product ? product : "");

    const version = queryParams.get(VERSION);
    setVersionValue(version ? version : "");

    const category = queryParams.get(CATEGORY);
    setCategoryValue(category ? category : "");

    const label = queryParams.get(LABEL);
    setLabelValue(label ? label : "");
  }, [location]);

  // Handle the items per page drop down
  const handleChangeItemsPerPage = (event: any) => {
    localStorage.setItem("itemsPerPage", event.target.value);
    setItemsPerPage(event.target.value);
    handleChangePage("", 1);
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete(PAGE);
    searchParams.delete(SEARCH);
    searchParams.delete(PRODUCT);
    searchParams.delete(VERSION);
    searchParams.delete(CATEGORY);
    searchParams.delete(LABEL);
    if (page !== null) {
      searchParams.set(PAGE, page.toString());
    }
    if (searchValue !== "") {
      searchParams.set(SEARCH, searchValue);
    }
    if (productValue !== "") {
      searchParams.set(PRODUCT, productValue);
    }
    if (versionValue !== "") {
      searchParams.set(VERSION, versionValue);
    }
    if (categoryValue !== "") {
      searchParams.set(CATEGORY, categoryValue);
    }
    if (labelValue !== "") {
      searchParams.set(LABEL, labelValue);
    }
    const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
    window.history.pushState({ path: newUrl }, "", newUrl);
  }, [
    page,
    searchValue,
    productValue,
    versionValue,
    categoryValue,
    labelValue,
  ]);

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage);
  };

  const handleSearchChange = (event: any) => {
    setSearchValue(event.target.value);
    setPage(1);
  };

  const handleProductChange = (event: any, value: string | null) => {
    setProductValue(value ? value : "");
    setVersionValue("");
    setCategoryValue("");
    setLabelValue("");
    setPage(1);
  };

  const handleVersionChange = (event: any, value: string | null) => {
    setVersionValue(value ? value : "");
    setPage(1);
  };

  const handleCategoryChange = (event: any, value: string | null) => {
    setCategoryValue(value ? value : "");
    setPage(1);
  };

  const handleLabelChange = (event: any, value: string | null) => {
    setLabelValue(value ? value : "");
    setPage(1);
  };

  return (
    <Box
      id="box"
      sx={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.palette.background.default,
        justifyContent: connectorLoading ? "center" : "flex-start",
        alignItems: "center",
        overflow: "none",
        minHeight: { xs: "calc(100vh - 56px)", sm: "calc(100vh - 64px)" },
      }}
    >
      <CustomSnackbar
        open={isSnackbarOpen}
        onClose={() => {
          setIsSnackbarOpen(false);
        }}
        message={getApiErrorMsg([
          connectorMetaError,
          connectorError,
          categoryError,
          labelError,
        ])}
        severity={"error"}
      />
      <Box
        sx={{
          flex: 1,
          width: "100%",
        }}
      >
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexDirection: {
                md: "row",
                xs: "column",
              },
              m: {
                md: "12px 20px 12px 20px",
                xs: "12px 20px 12px 20px",
              },
              flex: 1,
            }}
          >
            <FormControl
              component="form"
              fullWidth
              onSubmit={(e) => e.preventDefault()}
              sx={{
                width: {
                  md: "100%",
                  xs: "100%",
                  backgroundColor: theme.palette.background.paper,
                },
              }}
              variant="outlined"
              size="small"
            >
              <OutlinedInput
                placeholder="Search"
                value={searchValue}
                onChange={handleSearchChange}
                endAdornment={<SearchOutlined />}
              />
            </FormControl>
            <FormControl
              fullWidth
              sx={{
                width: {
                  md: "40%",
                  xs: "100%",
                  backgroundColor: theme.palette.background.paper,
                },
                ml: {
                  lg: 3,
                },
                mt: {
                  lg: 0,
                  xs: 2,
                },
              }}
              variant="outlined"
              size="small"
            >
              <Autocomplete
                loading={productLoading}
                size="small"
                value={productValue}
                onChange={(event, value) => handleProductChange(event, value)}
                options={products
                  .filter((product) => PRODUCT_MAP[product] != null)
                  .map((product) => PRODUCT_MAP[product])}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Product"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {productLoading ? (
                            <CircularProgress color="primary" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
                fullWidth
              />
            </FormControl>
            <FormControl
              fullWidth
              sx={{
                width: {
                  md: "40%",
                  xs: "100%",
                  backgroundColor: theme.palette.background.paper,
                },
                ml: {
                  lg: 3,
                },
                mt: {
                  lg: 0,
                  xs: 2,
                },
              }}
              variant="outlined"
              size="small"
            >
              <Autocomplete
                disabled={!productValue}
                loading={versionLoading}
                size="small"
                value={versionValue}
                onChange={(event, value) => handleVersionChange(event, value)}
                options={versions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Version"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {versionLoading ? (
                            <CircularProgress color="primary" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
                fullWidth
              />
            </FormControl>
            <FormControl
              fullWidth
              sx={{
                width: {
                  md: "40%",
                  xs: "100%",
                  backgroundColor: theme.palette.background.paper,
                },
                ml: {
                  lg: 3,
                },
                mt: {
                  lg: 0,
                  xs: 2,
                },
              }}
              variant="outlined"
              size="small"
            >
              <Autocomplete
                disabled={!productValue}
                loading={categoryLoading}
                size="small"
                value={categoryValue}
                onChange={(event, value) => handleCategoryChange(event, value)}
                options={categories}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Category"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {categoryLoading ? (
                            <CircularProgress color="primary" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
                fullWidth
              />
            </FormControl>
            <FormControl
              fullWidth
              sx={{
                width: {
                  md: "40%",
                  xs: "100%",
                  backgroundColor: theme.palette.background.paper,
                },
                ml: {
                  lg: 3,
                },
                mt: {
                  lg: 0,
                  xs: 2,
                },
              }}
              variant="outlined"
              size="small"
            >
              <Autocomplete
                disabled={!productValue}
                loading={labelLoading}
                size="small"
                value={labelValue}
                onChange={(event, value) => handleLabelChange(event, value)}
                options={labels}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Label"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {labelLoading ? (
                            <CircularProgress color="primary" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
                fullWidth
              />
            </FormControl>

            {/* TODO: Figure out better place to show the result count */}
            {/* <Typography
              variant="body2"
              color={theme.palette.success.main}
              sx={{
                width: {
                  md: "40%",
                  xs: "100%",
                  textAlign: "end",
                  backgroundColor: theme.palette.background.default,
                  fontWeight: 600,
                },
              }}
            >
              {(searchValue || productValue || categoryValue || labelValue) && (
                <>
                  {(connectorMeta as ConnectorMeta)?.totalCount} Results Found
                </>
              )}
            </Typography> */}
          </Box>
          {connectorLoading ? (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              <CircularProgress color="primary" />
            </div>
          ) : (
            <div style={{ position: "relative", marginTop: 20 }}>
              <Grid
                container
                spacing={2}
                sx={{
                  overflowY: "scroll",
                  maxHeight: "100%",
                  position: "relative",
                  pb: { xs: 2, md: 10 },
                  pr: 2,
                  pl: 2,
                  "&::-webkit-scrollbar": {
                    width: "0px",
                  }
                }}
              >
                {connectors.length > 0 &&
                  connectors.map((connector: Connector) => (
                    <ConnectorCard
                      key={connector.repoName}
                      connector={connector}
                    />
                  ))}
                {(connectorError != null || connectorMetaError != null) &&
                  connectors.length === 0 && (
                    <div
                      style={{
                        width: "100%",
                        height: "70vh",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          pb: 2,
                          fontWeight: 600,
                          color: theme.palette.error.main,
                        }}
                      >
                        Sorry, unable to load connectors at the moment. Please
                        try again later.
                      </Typography>
                    </div>
                  )}
                {!connectorError &&
                  !connectorMetaError &&
                  connectors.length === 0 && (
                    <Box
                      sx={{
                        width: "100%",
                        height: "60vh",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1.5,
                      }}
                    >
                      <SearchSharp
                        sx={{
                          fontSize: 48,
                          color: theme.palette.text.disabled,
                        }}
                      />
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 700, color: theme.palette.text.primary }}
                      >
                        No matching connectors found
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        Try adjusting your search or filters
                      </Typography>
                    </Box>
                  )}
              </Grid>
            </div>
          )}
          <Box
            sx={{
              position: { xs: "inherit", md: "fixed" },
              bottom: 0,
              width: "100%",
              backgroundColor: theme.palette.background.paper,
              pb: { lg: 2.4, xs: 0 },
              pt: { xs: 1 },
              height: { lg: "4vh", sx: "5vh" },
              borderTop: "0.1rem solid",
              borderColor: theme.palette.background.default,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "center", md: "flex-end" },
                justifyContent: "center",
              }}
            >
              <Pagination
                color="primary"
                siblingCount={isMobileScreen ? 1 : 2}
                count={Math.ceil(
                  (connectorMeta as ConnectorMeta)?.totalCount / itemsPerPage
                )}
                page={page}
                onChange={handleChangePage}
              />
              <Select
                color="primary"
                value={itemsPerPage}
                onChange={handleChangeItemsPerPage}
                variant="standard"
                size="small"
                displayEmpty
                sx={{
                  ml: { md: 3 },
                  mt: { xs: 1, md: 2 },
                  fontSize: { xs: 13 },
                }}
              >
                {ITEM_PER_PAGE_SIZES.map((size, key) => (
                  <MenuItem key={key} value={size} color="primary">
                    {size} Per Page
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box
              sx={{
                position: { xs: "inherit", md: "absolute" },
                bottom: 0,
                left: 0,
                width: "100%",
                pb: 0.2,
              }}
            >
              <PageFooterContent />
            </Box>
          </Box>
        </>
      </Box>
    </Box>
  );
};

export default Home;
